import {SpanIcon} from '@mazraeapp/react_icon_kit';
import {useCallback, useRef, useState} from 'react';
import ReactCrop, {centerCrop, Crop, PixelCrop} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import Button from '../../../../../components/button';

interface Props {
  file?: File | null;
  imageUrl?: string;
  onChange: (file: File | null) => void;
}

function centerFreeCrop(mediaWidth: number, mediaHeight: number) {
  return centerCrop(
    {
      unit: '%',
      width: 90,
      height: 90,
      x: 5,
      y: 5,
    },
    mediaWidth,
    mediaHeight,
  );
}

const NationalCardUploader = ({file, imageUrl, onChange}: Props) => {
  const [rawImage, setRawImage] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [showModal, setShowModal] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;

    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setRawImage(reader.result?.toString() || null);
      setShowModal(true);
    });
    reader.readAsDataURL(e.target.files[0]);
  };

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const {width, height} = e.currentTarget;
      setCrop(centerFreeCrop(width, height));
    },
    [],
  );

  const getCroppedImg = async () => {
    if (!completedCrop || !imgRef.current) return;

    const canvas = document.createElement('canvas');
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;

    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(
      imgRef.current,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height,
    );

    return new Promise<File | null>(resolve => {
      canvas.toBlob(
        blob => {
          if (!blob) return resolve(null);

          resolve(
            new File([blob], 'national-card.jpg', {
              type: 'image/jpeg',
            }),
          );
        },
        'image/jpeg',
        0.92,
      );
    });
  };

  const handleSave = async () => {
    const croppedFile = await getCroppedImg();
    if (croppedFile) {
      onChange(croppedFile);
      setShowModal(false);
      setRawImage(null);
    }
  };

  return (
    <>
      {file || imageUrl ? (
        <div className="relative w-full h-40 overflow-hidden border-2 border-dashed rounded-lg">
          <div
            className="absolute z-10 p-1 bg-white rounded cursor-pointer left-2 top-2"
            onClick={() => onChange(null)}
          >
            <SpanIcon className="text-lg text-red-600 icon-nameDelete" />
          </div>
          <img
            src={imageUrl || (file ? URL.createObjectURL(file) : '')}
            className="object-cover w-full h-full"
            alt="national-card"
          />
        </div>
      ) : (
        <label className="flex items-center justify-center h-32 bg-white border-2 border-dashed rounded cursor-pointer">
          <div className="flex flex-col items-center justify-center gap-2">
            <SpanIcon className="text-2xl text-gray-600 icon-nameImage-plus" />
            <span className="text-sm">بارگذاری تصویر کارت ملی</span>
          </div>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onSelectFile}
          />
        </label>
      )}

      {/* Crop Modal */}
      {showModal && rawImage && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black">
          {/* Header */}
          <div className="p-4 text-xs text-center text-gray-300">
            کارت را داخل کادر قرار دهید
          </div>

          <div className="flex items-center justify-center flex-1 p-6 overflow-auto">
            <div className="inline-block">
              <ReactCrop
                crop={crop}
                onChange={c => setCrop(c)}
                onComplete={c => setCompletedCrop(c)}
              >
                <img
                  ref={imgRef}
                  src={rawImage}
                  onLoad={onImageLoad}
                  className="max-w-full max-h-[70vh] object-contain"
                  alt="crop"
                />
              </ReactCrop>
            </div>
          </div>

          {/* Bottom Controls */}
          <div className="flex items-center justify-between gap-2 p-2 text-white bg-white rounded-t-lg">
            <Button
              onClick={handleSave}
              className="px-3 py-1 text-white rounded bg-green-main"
            >
              تایید
            </Button>

            <Button variant="outline" onClick={() => setShowModal(false)}>
              انصراف
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default NationalCardUploader;
